import S3UploadController from "./s3_upload_controller"

// Connects to data-controller="manual-import"
// Extends S3UploadController to reuse S3 upload and progress functionality
export default class extends S3UploadController {
  static targets = [
    ...S3UploadController.targets,
    "mappingSelect",
    "statusText",
    "errorContainer",
    "errorMessage",
    "successContainer",
    "successMessage"
  ]
  
  static values = {
    ...S3UploadController.values,
    tableDefinitionId: String
  }

  connect() {
    console.log("Manual Import controller connected")
  }

  async startImport() {
    const file = this.fileInputTarget.files[0]
    if (!file) {
      this.showError("Please select a file to upload")
      return
    }

    // Validate file size using parent's validation constant
    const maxSize = 100 * 1024 * 1024 // 100MB
    if (file.size > maxSize) {
      this.showError("File is too large. Maximum size is 100MB")
      return
    }

    try {
      this.disableForm()
      this.showProgressWithStatus("Creating import record...")
      
      // Step 1: Create Import record
      const importData = await this.createImportRecord()
      console.log("Import record created:", importData)
      
      // Step 2: Call API to create ImportTable and get presigned URL
      this.updateStatus("Preparing file upload...")
      const importTableData = await this.createImportTable(importData, file.name)
      console.log("Import table created:", importTableData)
      
      // Step 3: Upload file to S3 (uses parent's uploadToS3 and updateProgress methods)
      this.updateStatus("Uploading file...")
      await this.uploadToS3(importTableData.signed_s3_url, file, 'PUT')
      
      // Step 4: Check if we need to show preview (when no mapping was selected)
      const mappingKey = this.hasMappingSelectTarget ? this.mappingSelectTarget.value : null
      
      if (!mappingKey || mappingKey === '' || mappingKey === 'auto') {
        // Redirect to preview page for auto-matching
        this.updateStatus("Analyzing file...")
        window.location.href = `/import_tables/${importTableData.id}/mapping_preview`
      } else {
        // Mark upload as complete and trigger import job (existing mapping selected)
        this.updateStatus("Starting import process...")
        await this.markUploadComplete(importTableData.id)
        
        // Mark the import as upload_complete since all tables are uploaded
        await this.markImportUploadComplete(importData.import_id)
        
        // Show success - the ImportTableJob will set the import to completed when finished
        this.showSuccess(
          `Your file has been uploaded and the import is processing. ` +
          `<a href="/imports/${importData.import_id}" class="underline font-semibold">View import status</a>`
        )
        
      }
      
    } catch (error) {
      console.error('Import error:', error)
      this.showError(`Import failed: ${error.message}`)
      this.enableForm()
    }
  }

  async createImportRecord() {
    const response = await fetch(`/tables/${this.tableDefinitionIdValue}/import`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.csrfToken
      },
      body: JSON.stringify({
        correlation_code: `manual_${Date.now()}`
      })
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.message || 'Failed to create import record')
    }

    return await response.json()
  }

  async createImportTable(importData, filename) {
    const mappingKey = this.hasMappingSelectTarget ? this.mappingSelectTarget.value : null
    
    const payload = {
      import_id: importData.import_id,
      deployed_table_definition_id: importData.deployed_table_definition_id,
      correlation_code: `manual_${Date.now()}`,
      pattern: 'full',  // Manual imports always use full pattern (merge with deletes)
      // The server names the S3 object itself, so this is the only thing that
      // tells it whether it is about to receive a CSV, a workbook or a Parquet
      // file. Without it every upload is stored as .csv and read as one.
      filename: filename
    }
    
    if (mappingKey) {
      payload.import_mapping_key = mappingKey
    }

    const response = await fetch('/api/import_tables/new', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.csrfToken
      },
      body: JSON.stringify(payload)
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.message || 'Failed to create import table')
    }

    return await response.json()
  }

  async markUploadComplete(importTableId) {
    const response = await fetch(`/api/import_tables/${importTableId}/upload_complete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.csrfToken
      }
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.message || 'Failed to complete upload')
    }

    return await response.json()
  }

  async markImportUploadComplete(importId) {
    const response = await fetch(`/api/imports/${importId}/upload_complete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.csrfToken
      }
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.message || 'Failed to mark import upload complete')
    }

    return await response.json()
  }

  // Helper methods (extending parent's functionality)
  disableForm() {
    this.submitButtonTarget.disabled = true
    this.fileInputTarget.disabled = true
    if (this.hasMappingSelectTarget) {
      this.mappingSelectTarget.disabled = true
    }
  }

  enableForm() {
    this.submitButtonTarget.disabled = false
    this.fileInputTarget.disabled = false
    if (this.hasMappingSelectTarget) {
      this.mappingSelectTarget.disabled = false
    }
  }

  showProgressWithStatus(message) {
    this.hideError()
    this.hideSuccess()
    this.showProgress() // Call parent's showProgress
    this.updateStatus(message)
  }

  updateStatus(message) {
    if (this.hasStatusTextTarget) {
      this.statusTextTarget.textContent = message
    }
  }

  // Override parent's updateProgress to also update status text
  updateProgress(percent) {
    super.updateProgress(percent) // Call parent's implementation
  }

  showError(message) {
    this.hideProgress()
    this.hideSuccess()
    this.errorContainerTarget.classList.remove('hidden')
    this.errorMessageTarget.textContent = message
  }

  hideError() {
    this.errorContainerTarget.classList.add('hidden')
  }

  showSuccess(message) {
    this.hideProgress()
    this.hideError()
    this.successContainerTarget.classList.remove('hidden')
    this.successMessageTarget.innerHTML = message
  }

  hideSuccess() {
    this.successContainerTarget.classList.add('hidden')
  }
}
